import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import UndoRoundedIcon from '@mui/icons-material/UndoRounded'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded'
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded'
import { Tooltip } from '@mui/material'
import InfiniteScrollSentinel from 'components/InfiniteScrollSentinel'
import useFilePreview from 'features/bpDetails/Components/files/useFilePreview'
import { formatDate } from 'utils/date'
import {
  getMarkedFilesExport,
  unmarkFilesFromDeletion,
  downloadMarkedFilesCsv
} from 'services/fileService'

const PAGE = 25

const shortFolder = path => {
  if (!path) return null
  const parts = path.split('/').filter(Boolean)
  return parts.length > 2 ? `…/${parts.slice(-2).join('/')}` : parts.join('/')
}

const fullFolderPath = row => [row.sharepointCustomView, row.sharepointFolderPath].filter(Boolean).join(' / ')
const shortFolderPath = row => [row.sharepointCustomView, shortFolder(row.sharepointFolderPath)].filter(Boolean).join(' / ')

const SharePointCleansePanel = ({ bp, canEdit, onCountChange, onOpenInsight }) => {
  const { open: openPreview, previewingId, modal: previewModal } = useFilePreview(bp?.id)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')
  const [sortKey, setSortKey] = useState('markedAt')
  const [sortDir, setSortDir] = useState('desc')
  const [busyId, setBusyId] = useState(null)
  const [exporting, setExporting] = useState(false)
  const [visible, setVisible] = useState(PAGE)

  const report = useCallback(total => {
    if (onCountChange) onCountChange('sharepoint_cleanse', total)
  }, [onCountChange])

  const fetchMarked = useCallback(() => {
    setLoading(true)
    getMarkedFilesExport(bp.id, 'json')
      .then(res => {
        const list = res?.data?.data?.files || []
        setRows(list)
        report(list.length)
      })
      .catch(() => toast.error('Could not load the deletion list. Please try again.'))
      .finally(() => setLoading(false))
  }, [bp.id, report])

  useEffect(() => { fetchMarked() }, [fetchMarked])

  useEffect(() => { setVisible(PAGE) }, [query, sortKey, sortDir])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return rows
    return rows.filter(r =>
      (r.displayName || '').toLowerCase().includes(q) ||
      (r.sharepointFolderPath || '').toLowerCase().includes(q) ||
      (r.sharepointCustomView || '').toLowerCase().includes(q)
    )
  }, [rows, query])

  const sorted = useMemo(() => {
    const copy = [...filtered]
    copy.sort((a, b) => {
      let av
      let bv
      if (sortKey === 'displayName') {
        av = (a.displayName || '').toLowerCase()
        bv = (b.displayName || '').toLowerCase()
      } else {
        av = a.markedAt || ''
        bv = b.markedAt || ''
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1
      if (av > bv) return sortDir === 'asc' ? 1 : -1
      return 0
    })
    return copy
  }, [filtered, sortKey, sortDir])

  const visibleRows = sorted.slice(0, visible)

  const toggleSort = key => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir(key === 'displayName' ? 'asc' : 'desc')
    }
  }

  const unmark = async row => {
    setBusyId(row.id)
    try {
      await unmarkFilesFromDeletion(bp.id, [row.id])
      setRows(prev => {
        const next = prev.filter(r => r.id !== row.id)
        report(next.length)
        return next
      })
      toast.success('Removed from the deletion list')
    } catch (e) {
      toast.error('Could not unmark this file. Please try again.')
    } finally {
      setBusyId(null)
    }
  }

  const exportCsv = async () => {
    setExporting(true)
    try {
      const res = await downloadMarkedFilesCsv(bp.id)
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }))
      const link = document.createElement('a')
      link.href = url
      link.download = `sharepoint-cleanse-${bp.id}-${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (e) {
      toast.error('Could not export the list. Please try again.')
    } finally {
      setExporting(false)
    }
  }

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return null
    return sortDir === 'asc'
      ? <ArrowUpwardRoundedIcon style={{ fontSize: 13 }} />
      : <ArrowDownwardRoundedIcon style={{ fontSize: 13 }} />
  }

  if (loading) {
    return (
      <div>
        <Toolbar>
          <SearchBox><SearchRoundedIcon style={{ fontSize: 18, color: '#94a3b8' }} /><input disabled placeholder='Search by name or folder' /></SearchBox>
        </Toolbar>
        <Skeletons>{Array.from({ length: 6 }).map((_, i) => <SkelRow key={i} />)}</Skeletons>
      </div>
    )
  }

  return (
    <>
    <div>
      <Toolbar>
        <SearchBox>
          <SearchRoundedIcon style={{ fontSize: 18, color: '#94a3b8' }} />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder='Search by name or folder'
          />
        </SearchBox>
        <ExportBtn onClick={exportCsv} disabled={exporting || rows.length === 0}>
          <FileDownloadOutlinedIcon style={{ fontSize: 16 }} /> {exporting ? 'Preparing…' : 'Export list (CSV)'}
        </ExportBtn>
      </Toolbar>

      {rows.length === 0 ? (
        <Empty>
          <EmptyTitle>Nothing marked for deletion</EmptyTitle>
          <EmptySub>When you mark a SharePoint duplicate or unreadable file for deletion, it appears here so you can review the list and remove those files in SharePoint.</EmptySub>
        </Empty>
      ) : (
        <>
          <CountLine>Showing {visibleRows.length} of {filtered.length}{filtered.length !== rows.length ? ` (of ${rows.length} marked)` : ''}</CountLine>
          <Table>
            <THead>
              <TH style={{ width: '26%' }} $sortable onClick={() => toggleSort('displayName')}>File <SortIcon col='displayName' /></TH>
              <TH style={{ width: '28%' }}>Location in SharePoint</TH>
              <TH style={{ width: '14%' }}>Marked by</TH>
              <TH style={{ width: '12%' }} $sortable onClick={() => toggleSort('markedAt')}>Marked <SortIcon col='markedAt' /></TH>
              <TH style={{ minWidth: 230, flexShrink: 0 }} />
            </THead>
            {visibleRows.map(row => (
              <TRow key={row.id}>
                <TD style={{ width: '26%' }}><Name as='button' type='button' title='View document insight' onClick={() => onOpenInsight?.({ id: row.id, displayName: row.displayName })}>{row.displayName}</Name>{row.notes ? <Note>{row.notes}</Note> : null}</TD>
                <TD style={{ width: '28%' }}>
                  {fullFolderPath(row) ? (
                    <Tooltip
                      title={fullFolderPath(row)}
                      placement='top'
                      arrow
                      enterTouchDelay={0}
                      slotProps={{ tooltip: { sx: { maxWidth: 460, fontSize: 12, lineHeight: 1.5, wordBreak: 'break-word', whiteSpace: 'pre-wrap' } } }}
                    >
                      <Folder $hoverable>
                        <FolderOutlinedIcon style={{ fontSize: 14, color: '#94a3b8' }} />
                        <PathText>{shortFolderPath(row)}</PathText>
                      </Folder>
                    </Tooltip>
                  ) : (
                    <Folder>
                      <FolderOutlinedIcon style={{ fontSize: 14, color: '#94a3b8' }} />
                      <Muted>Unknown</Muted>
                    </Folder>
                  )}
                </TD>
                <TD style={{ width: '14%' }}><Muted>{row.markedByName || 'Someone'}</Muted></TD>
                <TD style={{ width: '12%' }}><Muted>{row.markedAt ? formatDate(row.markedAt) : '-'}</Muted></TD>
                <TD style={{ minWidth: 230, textAlign: 'right', flexShrink: 0 }}>
                  <RowBtns>
                    <RowBtn disabled={previewingId === row.id} onClick={() => openPreview({ id: row.id, displayName: row.displayName })} title='Preview this document'>
                      <VisibilityOutlinedIcon style={{ fontSize: 15 }} /> {previewingId === row.id ? 'Opening…' : 'Preview'}
                    </RowBtn>
                    {row.sharepointWebUrl && (
                      <RowLink href={row.sharepointWebUrl} target='_blank' rel='noopener noreferrer' title='Open in SharePoint'>
                        <OpenInNewRoundedIcon style={{ fontSize: 15 }} /> Open
                      </RowLink>
                    )}
                    {canEdit && (
                      <RowBtn disabled={busyId === row.id} onClick={() => unmark(row)} title='Remove from the deletion list'>
                        <UndoRoundedIcon style={{ fontSize: 15 }} /> {busyId === row.id ? 'Removing…' : 'Unmark'}
                      </RowBtn>
                    )}
                  </RowBtns>
                </TD>
              </TRow>
            ))}
          </Table>
          <InfiniteScrollSentinel
            hasMore={visible < sorted.length}
            loading={false}
            onLoadMore={() => setVisible(v => v + PAGE)}
          />
        </>
      )}
    </div>
    {previewModal}
    </>
  )
}

export default SharePointCleansePanel

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`

const SearchBox = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  flex: 1;
  min-width: 220px;
  max-width: 420px;

  input {
    border: none;
    outline: none;
    font-size: 14px;
    width: 100%;
    background: transparent;
    color: #1e293b;
  }
`

const ExportBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid #33becc;
  background: #fff;
  color: #0f6f78;
  font-size: 13px;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 150ms ease;

  &:hover:not(:disabled) { background: #f0fbfc; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`

const CountLine = styled.div`
  font-size: 12.5px;
  color: #64748b;
  margin-bottom: 8px;
`

const Table = styled.div`
  display: flex;
  flex-direction: column;
`

const THead = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 10px;
  border-bottom: 1px solid #e2e8f0;
`

const TH = styled.div`
  font-size: 11.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  cursor: ${({ $sortable }) => ($sortable ? 'pointer' : 'default')};
  user-select: none;

  &:hover { color: ${({ $sortable }) => ($sortable ? '#334155' : '#64748b')}; }
`

const TRow = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
  border-bottom: 1px solid #f1f5f9;

  &:hover { background: #f8fafc; }
`

const TD = styled.div`
  font-size: 13.5px;
  color: #1e293b;
  padding-right: 8px;
  overflow: hidden;
`

const Name = styled.div`
  font: inherit;
  display: block;
  width: 100%;
  text-align: left;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  font-weight: 600;
  color: #33becc;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  &:hover { text-decoration: underline; }
`

const Note = styled.div`
  font-size: 12px;
  color: #94a3b8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const Folder = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #475569;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  cursor: ${({ $hoverable }) => ($hoverable ? 'help' : 'default')};
`

const PathText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  border-bottom: 1px dotted #cbd5e1;
`

const Muted = styled.span`
  color: #64748b;
`

const RowBtns = styled.div`
  display: inline-flex;
  gap: 6px;
  justify-content: flex-end;
`

const btnBase = `
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 9px;
  border-radius: 7px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
  transition: background-color 150ms ease;
`

const RowBtn = styled.button`
  ${btnBase}
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #334155;

  &:hover:not(:disabled) { background: #f1f5f9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`

const RowLink = styled.a`
  ${btnBase}
  border: 1px solid #cfe9ec;
  background: #f0fbfc;
  color: #0f6f78;

  &:hover { background: #e2f6f8; }
`

const Empty = styled.div`
  text-align: center;
  padding: 48px 24px;
`

const EmptyTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 6px;
`

const EmptySub = styled.div`
  font-size: 13.5px;
  color: #64748b;
  max-width: 460px;
  margin: 0 auto;
  line-height: 1.5;
`

const Skeletons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const SkelRow = styled.div`
  height: 44px;
  border-radius: 8px;
  background: #f1f5f9;
  animation: pulse 1.4s ease-in-out infinite;

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`
